import Product from './Product';
import ProductSwatches from './ProductSwatches';
import { openModal } from '../../helpers/modals';
import showQuickshop from '../../helpers/showQuickshop';
import { cartApi, getTranslation, updateCart } from '../../helpers';

const selectors = {
  quickShopTrigger: '[data-quickshop-trigger]',
  modalProduct: '[data-product]',
  modalContent: 'quickshop-content',
  swatchInput: '[data-product-item-swatch]',
  swatchesContainer: '[data-product-item-swatches]',
  productLinks: '[data-product-item-link]',
  imageLink: '[data-product-item-image-link]',
  titleLink: '[data-product-item-title-link]',
  price: '[data-product-item-price]',
  badges: '[data-product-item-badges]',
  wishlistButton: 'ooo-wl-wishlist-button',
  addToCartForm: '[data-product-item-form]',
  addToCartButton: '[data-product-item-add-to-cart]',
  variantSelect: '[data-product-item-variant-select]',
  optionSelect: '[data-product-item-option-select]',
};

const MODAL_ID = 'quickshop';
const FRAGMENT_SECTION_ID = 'card-fragment';

const productCardCache = new Map();

/**
 * A single product item in a collection / list of products
 *
 * @export
 * @class ProductItem
 */
export default class ProductItem {
  /**
   * Creates an instance of ProductItem.
   * @param {HTMLElement} el
   */
  constructor(el) {
    this.el = el;
    this._activeSwapToken = 0;

    this._initQuickShop();
    this._initAddToCart();
    this._initSwatchCarousel();
    this._initSwatchSwap();
  }

  /**
   * Bind add-to-cart form to submit via AJAX
   */
  _initAddToCart() {
    const form = this.el.querySelector(selectors.addToCartForm);
    if (!form) {
      return;
    }

    form.addEventListener('submit', this._onAddToCart);
    this._initVariantOptionSync(form);
  }

  /**
   * When visible option selects change, sync the hidden variant id and button state
   */
  _initVariantOptionSync(form) {
    const variantSelect = form.querySelector(selectors.variantSelect);
    const optionSelects = form.querySelectorAll(selectors.optionSelect);
    const addButton = form.querySelector(selectors.addToCartButton);

    if (!variantSelect || !optionSelects.length) {
      return;
    }

    const syncVariant = () => {
      const selectedByPosition = {};
      optionSelects.forEach(select => {
        const pos = select.dataset.optionPosition;
        selectedByPosition[pos] = select.value;
      });

      const variantOptions = [...variantSelect.options];
      const matching = variantOptions.find(opt => {
        for (const pos in selectedByPosition) {
          const attr = `data-option${pos}`;
          if (opt.getAttribute(attr) !== selectedByPosition[pos]) {
            return false;
          }
        }
        return true;
      });

      if (matching) {
        variantSelect.value = matching.value;
        const isUnavailable = matching.hasAttribute('data-unavailable');
        if (addButton) {
          addButton.disabled = isUnavailable;
          addButton.textContent = isUnavailable
            ? getTranslation('products.sold_out')
            : getTranslation('products.add');
        }
      }
    };

    optionSelects.forEach(select => {
      select.addEventListener('change', syncVariant);
    });
    syncVariant();
  }

  _onAddToCart = e => {
    e.preventDefault();
    const form = e.target;
    const button = form.querySelector(selectors.addToCartButton);
    const originalText = button?.textContent;

    if (button) {
      button.disabled = true;
      button.textContent = getTranslation('products.adding');
    }

    cartApi
      .add(form)
      .then(response => {
        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }
        if (button) {
          button.textContent = getTranslation('products.added');
          setTimeout(() => {
            button.disabled = false;
            button.textContent = originalText;
          }, 1500);
        }
      })
      .catch(() => {
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      });
  };

  _initModal(data, quickShopUrl) {
    const quickshopHtml = showQuickshop(data);
    const quickShopContent = document.getElementById(selectors.modalContent);
    quickShopContent.innerHTML = quickshopHtml;

    openModal(MODAL_ID);

    const product = quickShopContent.querySelector(selectors.modalProduct);

    // Create a new Product instance for the requested quickshop product
    new Product(product, {
      isQuickShop: true,
      quickShopUrl,
      modalId: MODAL_ID,
    });
  }

  /**
   * Bind quickshop to a trigger
   */
  _initQuickShop() {
    const trigger = this.el.querySelector(selectors.quickShopTrigger);

    if (!trigger) {
      return;
    }

    trigger.addEventListener('click', () => {
      const { productUrl } = this.el.dataset;

      const quickShopUrl = `${productUrl}?section_id=${window.quickshopSectionId}`;

      fetch(quickShopUrl)
        .then(response => response.text())
        .then(data => {
          this._initModal(data, quickShopUrl);
        });
    });
  }

  /**
   * Swap product-card content when a swatch is selected.
   */
  _initSwatchSwap() {
    this.el.addEventListener('change', e => {
      const input = e.target;
      if (!input || !(input instanceof HTMLInputElement)) {
        return;
      }

      if (!input.matches(selectors.swatchInput)) {
        return;
      }

      const handle = input.dataset.swatchProductUrl;
      if (!handle) {
        return;
      }

      this._swapToHandle(handle);
    });
  }

  _initSwatchCarousel() {
    const container = this.el.querySelector(selectors.swatchesContainer);
    if (container) {
      new ProductSwatches(container);
    }
  }

  _swapToHandle(handle) {
    const isCached = productCardCache.has(handle);
    const swapToken = (this._activeSwapToken += 1);

    if (!isCached) {
      this.el.setAttribute('data-product-item-loading', 'true');
    }

    this._getCardData(handle)
      .then(cardData => {
        if (swapToken !== this._activeSwapToken) {
          return;
        }

        if (!cardData) {
          return;
        }

        this._swapCardContent(cardData);
      })
      .finally(() => {
        if (swapToken !== this._activeSwapToken) {
          return;
        }

        this.el.removeAttribute('data-product-item-loading');
      });
  }

  _getCardData(handle) {
    const cached = productCardCache.get(handle);
    if (cached) {
      return Promise.resolve(cached);
    }

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${handle}?section_id=${FRAGMENT_SECTION_ID}`;

    return fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const parsedHTML = new DOMParser().parseFromString(
          responseText,
          'text/html',
        );

        const newCard = parsedHTML.querySelector('[data-product-item]');
        if (!newCard) {
          return null;
        }

        const newProductUrl = newCard.dataset.productUrl;
        if (!newProductUrl) {
          return null;
        }

        const imageLink = newCard.querySelector(selectors.imageLink);
        const titleLink = newCard.querySelector(selectors.titleLink);
        const price = newCard.querySelector(selectors.price);
        const badges = newCard.querySelector(selectors.badges);
        const wishlistButton = newCard.querySelector(selectors.wishlistButton);

        const cardData = {
          url: newProductUrl,
          imageHtml: imageLink ? imageLink.innerHTML : '',
          title: titleLink ? titleLink.textContent : '',
          priceHtml: price ? price.innerHTML : '',
          badgesHtml: badges ? badges.innerHTML : '',
          wishlistHtml: wishlistButton ? wishlistButton.outerHTML : '',
        };

        productCardCache.set(handle, cardData);
        return cardData;
      })
      .catch(() => null);
  }

  _swapCardContent({
    url,
    title,
    imageHtml,
    priceHtml,
    badgesHtml,
    wishlistHtml,
  }) {
    // Used by quickshop.
    this.el.dataset.productUrl = url;

    const links = this.el.querySelectorAll(selectors.productLinks);
    links.forEach(link => {
      link.setAttribute('href', url);
    });

    const imageLink = this.el.querySelector(selectors.imageLink);
    if (imageLink) {
      imageLink.setAttribute('href', url);
      imageLink.innerHTML = imageHtml || '';
    }

    const titleLink = this.el.querySelector(selectors.titleLink);
    if (titleLink) {
      titleLink.setAttribute('href', url);
      titleLink.textContent = title || '';
    }

    const price = this.el.querySelector(selectors.price);
    if (price) {
      price.innerHTML = priceHtml || '';
    }

    const badges = this.el.querySelector(selectors.badges);
    if (badges) {
      badges.innerHTML = badgesHtml || '';
    }

    const wishlistButton = this.el.querySelector(selectors.wishlistButton);
    if (wishlistButton && wishlistHtml) {
      wishlistButton.outerHTML = wishlistHtml;
    }
  }
}
